import { useState, useMemo, useEffect, useRef } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { t, fmt, getAppLocale } from '@/i18n';
import { CapacityBar, confirmationDisplay } from './eventCapacity';

export interface CalendarEvent {
  id: number;
  title: string;
  slug: string;
  start_at: string | null;
  end_at: string | null;
  venue_details: string;
  status: string;
  max_capacity?: number | null;
  min_capacity?: number | null;
  confirmed_count?: number;
  confirmation_status?: string;
  spots_remaining?: number | null;
}

export interface CalendarProps {
  events: CalendarEvent[];
  onEventClick: (event: CalendarEvent) => void;
  loading?: boolean;
  refreshing?: boolean;
  onMonthChange: (year: number, month: number) => void;
  headerActions?: React.ReactNode;
  /** Month to open on (1-12). Defaults to the current month. */
  initialMonth?: number;
  /** Year to open on. Defaults to the current year. */
  initialYear?: number;
  /**
   * The next upcoming trip's month (1-based). When the displayed month is empty
   * and this points elsewhere, a "Jump to next trip" shortcut is shown (issue #398).
   */
  nextTripMonth?: { year: number; month: number } | null;
}

const WEEKDAYS = [t('Mon'), t('Tue'), t('Wed'), t('Thu'), t('Fri'), t('Sat'), t('Sun')];

interface DayCell {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  events: CalendarEvent[];
}

function buildMonthGrid(year: number, month: number, events: CalendarEvent[]): DayCell[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const firstOfMonth = new Date(year, month, 1);
  const startDow = (firstOfMonth.getDay() + 6) % 7; // 0=Mon
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const totalCells = Math.ceil((startDow + daysInMonth) / 7) * 7;

  const gridStart = new Date(firstOfMonth);
  gridStart.setDate(gridStart.getDate() - startDow);

  const cells: DayCell[] = [];
  for (let i = 0; i < totalCells; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    d.setHours(0, 0, 0, 0);

    const isCurrentMonth = d.getMonth() === month && d.getFullYear() === year;
    const isToday = d.getTime() === today.getTime();

    const dayEvents = events.filter((ev) => {
      if (!ev.start_at) return false;
      const evDate = new Date(ev.start_at);
      return (
        evDate.getFullYear() === d.getFullYear() &&
        evDate.getMonth() === d.getMonth() &&
        evDate.getDate() === d.getDate()
      );
    });

    cells.push({ date: d, isCurrentMonth, isToday, events: dayEvents });
  }

  return cells;
}

function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString(getAppLocale(), { hour: 'numeric', minute: '2-digit' });
}

export default function Calendar({
  events,
  onEventClick,
  loading = false,
  refreshing = false,
  onMonthChange,
  headerActions,
  initialMonth,
  initialYear,
  nextTripMonth,
}: CalendarProps) {
  const now = new Date();
  const [year, setYear] = useState(initialYear ?? now.getFullYear());
  const [month, setMonth] = useState(initialMonth != null ? initialMonth - 1 : now.getMonth());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  // Selected-day detail panel: nudge it into view + briefly flash on selection (mobile discoverability).
  const detailRef = useRef<HTMLDivElement>(null);
  const [flashPanel, setFlashPanel] = useState(false);
  const didAutoSelectRef = useRef(false); // one-shot mount-time auto-select
  const suppressNudgeRef = useRef(false); // skip the nudge for the mount-time selection

  const cells = useMemo(() => buildMonthGrid(year, month, events), [year, month, events]);

  const monthHasEvents = useMemo(
    () => cells.some((c) => c.isCurrentMonth && c.events.length > 0),
    [cells],
  );

  // Show the "jump to next trip" shortcut when this month is empty and the next trip is
  // strictly after the displayed month (so the shortcut always points forward).
  const displayedIndex = year * 12 + month;
  const nextTripIndex = nextTripMonth ? nextTripMonth.year * 12 + (nextTripMonth.month - 1) : null;
  const showJump = !monthHasEvents && nextTripIndex != null && nextTripIndex > displayedIndex;

  const selectedDayEvents = useMemo(() => {
    if (!selectedDate) return [];
    const cell = cells.find(
      (c) => c.date.toDateString() === selectedDate && c.isCurrentMonth,
    );
    return cell?.events ?? [];
  }, [selectedDate, cells]);

  // Open pre-selected on the first current-month trip day, so the panel is populated on load and
  // self-demonstrates the affordance. One-shot; the mount-time selection is exempt from the nudge.
  useEffect(() => {
    if (didAutoSelectRef.current) return;
    const firstWithEvents = cells.find((c) => c.isCurrentMonth && c.events.length > 0);
    if (!firstWithEvents) return;
    didAutoSelectRef.current = true;
    suppressNudgeRef.current = true;
    setSelectedDate(firstWithEvents.date.toDateString());
  }, [cells]);

  // On selection, nudge the panel into view (only when partly hidden — block:'nearest') and briefly
  // flash it. Desktop, where the panel is already visible, never jumps. Respects reduced-motion.
  useEffect(() => {
    if (!selectedDate) return;
    if (suppressNudgeRef.current) {
      suppressNudgeRef.current = false;
      return;
    }
    const el = detailRef.current;
    if (!el) return;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'nearest' });
    setFlashPanel(true);
    const timer = window.setTimeout(() => setFlashPanel(false), 1100);
    return () => window.clearTimeout(timer);
  }, [selectedDate]);

  function goToMonth(y: number, m: number) {
    setMonth(m);
    setYear(y);
    setSelectedDate(null);
    onMonthChange(y, m + 1);
  }

  function nav(direction: -1 | 1) {
    let m = month + direction;
    let y = year;
    if (m < 0) { m = 11; y--; }
    else if (m > 11) { m = 0; y++; }
    goToMonth(y, m);
  }

  function goToday() {
    const today = new Date();
    goToMonth(today.getFullYear(), today.getMonth());
  }

  const isCurrentMonthView = year === now.getFullYear() && month === now.getMonth();

  if (loading) {
    return <CalendarSkeleton />;
  }

  return (
    <div className="relative rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden dark:border-gray-800 dark:bg-gray-950">
      {/* ── Refresh overlay ── */}
      {refreshing && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 dark:bg-gray-950/60">
          <svg className="h-6 w-6 animate-spin text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        </div>
      )}
      {/* ── Header ── */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-800">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          {new Date(year, month).toLocaleDateString(getAppLocale(), { year: 'numeric', month: 'long' })}
        </h2>
        <div className="flex items-center gap-2">
          {headerActions}
          {!isCurrentMonthView && (
            <button
              type="button"
              onClick={goToday}
              className="rounded-md bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-200 transition-colors dark:bg-white/5 dark:text-gray-300 dark:hover:bg-white/10"
            >
              {t('Today')}
            </button>
          )}
          <button
            type="button"
            onClick={() => nav(-1)}
            className="rounded-md p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors dark:hover:text-white dark:hover:bg-white/10"
          >
            <ChevronLeftIcon className="h-5 w-5" />
            <span className="sr-only">{t('Previous month')}</span>
          </button>
          <button
            type="button"
            onClick={() => nav(1)}
            className="rounded-md p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors dark:hover:text-white dark:hover:bg-white/10"
          >
            <ChevronRightIcon className="h-5 w-5" />
            <span className="sr-only">{t('Next month')}</span>
          </button>
        </div>
      </div>

      {/* ── Empty month → jump to next trip ── */}
      {showJump && nextTripMonth && (
        <div className="flex items-center justify-between gap-3 border-b border-teal-100 bg-teal-50 px-4 py-3 dark:border-teal-500/20 dark:bg-teal-500/10 sm:px-6">
          <span className="text-sm text-gray-600 dark:text-gray-300">{t('No trips this month.')}</span>
          <button
            type="button"
            onClick={() => goToMonth(nextTripMonth.year, nextTripMonth.month - 1)}
            className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-teal-700 transition-colors hover:text-teal-900 dark:text-teal-300 dark:hover:text-teal-100"
          >
            {t('Jump to next trip')} <span aria-hidden="true">→</span>
          </button>
        </div>
      )}

      {/* ── Weekday headers ── */}
      <div className="grid grid-cols-7 text-center text-xs font-medium uppercase tracking-wider text-gray-500 border-b border-gray-200 dark:border-gray-800">
        {WEEKDAYS.map((d, i) => (
          <div key={d} className={`py-2 ${i >= 5 ? 'text-teal-600' : ''}`}>{d}</div>
        ))}
      </div>

      {/* ── Day grid ── */}
      <div className="grid grid-cols-7 border-l border-gray-200 dark:border-gray-800">
        {cells.map((cell, idx) => {
          const dateStr = cell.date.toDateString();
          const isSelected = selectedDate === dateStr && cell.isCurrentMonth;
          const hasEvents = cell.events.length > 0;

          return (
            <div
              key={idx}
              className={`min-h-[3.25rem] sm:min-h-[6.5rem] border-b border-r border-gray-200 bg-white p-2 dark:border-gray-800 dark:bg-gray-950/50${
                hasEvents && cell.isCurrentMonth ? ' cursor-pointer' : ''
              }${
                isSelected ? ' !bg-gray-100 dark:!bg-gray-800/60' : ''
              }`}
              onClick={() => {
                if (cell.isCurrentMonth && hasEvents) {
                  setSelectedDate(isSelected ? null : dateStr);
                }
              }}
            >
              <span
                className={`text-xs ${
                  !cell.isCurrentMonth
                    ? 'text-gray-300 dark:text-gray-600'
                    : hasEvents
                      ? 'font-medium text-gray-900 dark:text-gray-200'
                      : 'text-gray-400 dark:text-gray-500'
                }`}
              >
                {cell.date.getDate()}
              </span>

              {/* ── Mobile: dots only ── */}
              {hasEvents && cell.isCurrentMonth && (
                <div className="flex gap-0.5 mt-1 sm:hidden">
                  {cell.events.slice(0, 3).map((ev) => (
                    <span
                      key={ev.id}
                      className={`h-1.5 w-1.5 rounded-full ${dotColor(ev.status)}`}
                    />
                  ))}
                </div>
              )}

              {/* ── Desktop: event rows + capacity bars ── */}
              {cell.isCurrentMonth && (
                <div className="hidden sm:block">
                  {cell.events.slice(0, 2).map((ev) => {
                    const cap = ev.max_capacity ?? 0;
                    const filled = ev.confirmed_count ?? 0;
                    const pct = cap > 0 ? Math.round((filled / cap) * 100) : 0;
                    const isDraft = ev.status === 'draft';

                    return (
                      <div
                        key={ev.id}
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); onEventClick(ev); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onEventClick(ev); } }}
                        className="cursor-pointer"
                      >
                        <div className="mt-1">
                          <div className={`flex items-center gap-1 rounded px-1 py-0.5 text-[11px] ${isDraft ? 'text-gray-400 dark:text-gray-400' : 'text-gray-600 dark:text-gray-300'}`}>
                            <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${dotColor(ev.status)}`} />
                            <span className="truncate">
                              {ev.start_at && (
                                <span className={`mr-0.5 ${isDraft ? 'text-gray-400 dark:text-gray-600' : 'text-gray-400 dark:text-gray-500'}`}>
                                  {formatTime(ev.start_at)}
                                </span>
                              )}
                              {ev.title}
                            </span>
                          </div>
                        </div>

                        {cap > 0 && !isDraft && (
                          <div className="mt-1 px-1">
                            <div className={`flex items-center gap-1 text-[10px] ${barTextColor(pct)}`}>
                              <span>{filled}/{cap}</span>
                              <div className="h-1 flex-1 rounded-full bg-gray-200 overflow-hidden dark:bg-gray-800">
                                <div
                                  className={`h-full rounded-full ${barFillColor(pct)}`}
                                  style={{ width: `${Math.min(pct, 100)}%` }}
                                />
                              </div>
                            </div>
                          </div>
                        )}

                        {isDraft && (
                          <div className="mt-1 px-1">
                            <span className="text-[10px] text-gray-400 dark:text-gray-600">{t('Draft')}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {cell.events.length > 2 && (
                    <div className="mt-1 px-1 text-[10px] text-gray-500">
                      +{cell.events.length - 2} {t('more')}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Selected-day detail — every event on the day, with capacity (all breakpoints) ── */}
      {selectedDate && selectedDayEvents.length > 0 && (
        <div
          ref={detailRef}
          className={`border-t border-gray-200 bg-gray-50 p-4 transition-shadow duration-700 dark:border-gray-800 dark:bg-gray-900/60 sm:p-5${
            flashPanel ? ' ring-2 ring-inset ring-teal-500/50 dark:ring-teal-400/50' : ''
          }`}
        >
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              {new Date(selectedDate).toLocaleDateString(getAppLocale(), {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
              })}
            </h3>
            <span className="text-xs text-gray-400 dark:text-gray-500">
              {selectedDayEvents.length === 1
                ? t('1 trip')
                : fmt('%(count)s trips', { count: selectedDayEvents.length })}
            </span>
          </div>
          <div className="space-y-2.5">
            {selectedDayEvents.map((ev) => {
              // For non-published events (dashboard shows draft/closed/cancelled) the dot and label
              // follow the event status — matching the grid — instead of the confirmation model.
              const isPublished = ev.status === 'published';
              const isDraft = ev.status === 'draft';
              const cs = confirmationDisplay(ev);
              const dot = isPublished ? cs.dot : dotColor(ev.status);
              const label = isPublished ? cs.label : statusLabel(ev.status);
              const labelTone = isPublished ? cs.tone : 'text-gray-500 dark:text-gray-400';
              const multiDay =
                ev.start_at != null &&
                ev.end_at != null &&
                new Date(ev.start_at).toDateString() !== new Date(ev.end_at).toDateString();
              return (
                <button
                  key={ev.id}
                  type="button"
                  onClick={() => onEventClick(ev)}
                  className="flex w-full items-center gap-3 rounded-xl border border-gray-200 bg-white p-3 text-left transition-colors hover:border-teal-300 hover:shadow-sm dark:border-gray-800 dark:bg-gray-950 dark:hover:border-teal-500/50"
                >
                  <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${dot}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">{ev.title}</p>
                      <span className="shrink-0 text-[11px] font-medium text-gray-400 dark:text-gray-500">
                        {multiDay ? t('multi-day') : ev.start_at ? formatTime(ev.start_at) : ''}
                      </span>
                    </div>
                    <div className="mt-1.5 flex items-center gap-3">
                      {/* Draft events have no live capacity, matching the grid cell. */}
                      {!isDraft && (
                        <div className="flex-1">
                          <CapacityBar event={ev} />
                        </div>
                      )}
                      {label && <span className={`shrink-0 text-[10px] font-medium ${labelTone}`}>{label}</span>}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── helpers — exact colors from prototype ── */

function dotColor(status: string): string {
  switch (status) {
    case 'published': return 'bg-teal-500';
    case 'draft':     return 'bg-gray-500';
    case 'closed':    return 'bg-amber-500';
    case 'cancelled': return 'bg-red-500';
    default:          return 'bg-gray-500';
  }
}

function statusLabel(status: string): string {
  switch (status) {
    case 'draft':     return t('Draft');
    case 'closed':    return t('Closed');
    case 'cancelled': return t('Cancelled');
    default:          return '';
  }
}

function barFillColor(pct: number): string {
  if (pct >= 100) return 'bg-red-500/60';
  if (pct >= 85)  return 'bg-amber-500/60';
  return 'bg-teal-500/60';
}

function barTextColor(pct: number): string {
  if (pct >= 100) return 'text-red-400';
  if (pct >= 85)  return 'text-amber-500';
  return 'text-gray-500';
}

/* ── skeleton ── */

function CalendarSkeleton() {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden animate-pulse dark:border-gray-800 dark:bg-gray-950">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-800">
        <div className="h-6 w-32 rounded bg-gray-200 dark:bg-gray-800" />
        <div className="flex gap-2">
          <div className="h-8 w-16 rounded bg-gray-200 dark:bg-gray-800" />
          <div className="h-8 w-8 rounded bg-gray-200 dark:bg-gray-800" />
          <div className="h-8 w-8 rounded bg-gray-200 dark:bg-gray-800" />
        </div>
      </div>
      <div className="grid grid-cols-7 border-b border-gray-200 dark:border-gray-800">
        {Array.from({ length: 7 }).map((_, i) => (
          <div key={i} className="py-2 text-center">
            <div className="mx-auto h-3 w-6 rounded bg-gray-200 dark:bg-gray-800" />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 border-l border-gray-200 dark:border-gray-800">
        {Array.from({ length: 35 }).map((_, i) => (
          <div key={i} className="min-h-[3.25rem] sm:min-h-[6.5rem] border-b border-r border-gray-200 bg-white p-2 dark:border-gray-800 dark:bg-gray-950/50">
            <div className="h-3 w-4 rounded bg-gray-200 dark:bg-gray-800" />
          </div>
        ))}
      </div>
    </div>
  );
}
